<template>
  <div class="payment-conditions-card">
    <div class="payment-section-header">
      <i class="el-icon-wallet"></i>
      <span>Condiciones de pago</span>
    </div>

    <!-- Tipo de pago -->
    <el-radio-group
      v-model="methodPayment"
      class="payment-radio-group"
      @change="onMethodPaymentChange"
    >
      <el-radio-button :label="1">De contado</el-radio-button>
      <el-radio-button :label="2">Crédito</el-radio-button>
    </el-radio-group>

    <!-- ═══════ DE CONTADO ═══════ -->
    <transition name="slide-fade">
      <div v-if="methodPayment === 1" class="panel-contado" key="contado">
        <i class="el-icon-success"></i>
        Pago inmediato — sin recordatorios.
      </div>
    </transition>

    <!-- ═══════ CRÉDITO ═══════ -->
    <transition name="slide-fade">
      <div v-if="methodPayment === 2" class="panel-credito" key="credito">
        <b-row class="mb-3">
          <b-col xs="12" sm="6">
            <label class="field-label">Plazo de crédito</label>
            <select
              class="form-control form-control-sm"
              v-model="limitCredit"
              @change="onLimitCreditChange"
            >
              <option value="" disabled>Seleccionar plazo</option>
              <option
                v-for="opt in limitCredits"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </b-col>
          <b-col xs="12" sm="6" v-if="limitCredit === 'others'">
            <label class="field-label">Fecha de vencimiento</label>
            <input
              type="date"
              class="form-control form-control-sm"
              v-model="limitCreditAt"
              @change="emitAll"
            />
          </b-col>
        </b-row>

        <!-- Switch de cobros automáticos -->
        <div v-if="limitCredit" class="cobro-switch-section">
          <div class="cobro-switch-row">
            <el-switch
              v-model="enableReminders"
              active-color="#3fdbbf"
              inactive-color="#dcdfe6"
              @change="onToggleReminders"
            ></el-switch>
            <span class="cobro-switch-label">Cobro automático</span>
            <el-tooltip placement="top" effect="light">
              <div slot="content" class="tooltip-cobro">
                Se enviarán recordatorios automáticos de cobro:<br />
                <strong>3 días antes</strong> del vencimiento,<br />
                <strong>el día del vencimiento</strong>, y<br />
                <strong>cada viernes</strong> posterior hasta completar 60
                días.<br /><br />
                El cliente puede confirmar su pago directamente desde el correo.
                También puedes desactivar esta opción en cualquier momento desde
                la factura.
              </div>
              <i class="el-icon-info cobro-info-icon"></i>
            </el-tooltip>
          </div>
          <transition name="slide-fade">
            <div v-if="enableReminders" class="cobro-active-badge">
              <i class="el-icon-bell"></i>
              Recordatorios activos por 60 días
            </div>
          </transition>
        </div>
      </div>
    </transition>

    <!-- ═══════ ANTICIPO ═══════ -->
    <transition name="slide-fade">
      <div v-if="methodPayment === 3" class="panel-anticipo" key="anticipo">
        <label class="field-label">Observaciones</label>
        <el-input
          v-model="observationAdvance"
          type="textarea"
          :rows="2"
          placeholder="Ej: 60% al inicio, 40% contra entrega del proyecto..."
          size="small"
          class="mb-3"
          @input="emitAll"
        ></el-input>

        <!-- Cuotas de pago -->
        <div class="installments-section">
          <div class="installments-header">
            <span class="field-label m-0">
              <i class="el-icon-s-grid"></i> Cuotas de pago
            </span>
            <span
              :class="totalPercentage === 100 ? 'pct-ok' : 'pct-warn'"
              class="pct-badge"
            >
              {{ totalPercentage }}%
            </span>
          </div>

          <!-- Plantillas rápidas -->
          <div class="quick-tpl">
            <button
              type="button"
              v-for="tpl in quickTemplates"
              :key="tpl.key"
              :class="['tpl-btn', { active: activeTpl === tpl.key }]"
              @click="applyTemplate(tpl.key)"
            >
              {{ tpl.label }}
            </button>
            <button
              type="button"
              class="tpl-btn tpl-custom"
              @click="addInstallment"
            >
              <i class="el-icon-plus"></i> Personalizar
            </button>
          </div>

          <!-- Filas de cuotas -->
          <div
            v-for="(inst, index) in localInstallments"
            :key="'inst-' + index"
            class="inst-row"
          >
            <div class="inst-row-main">
              <div class="inst-field inst-label">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="localInstallments[index].label"
                  :placeholder="getDefaultLabel(index)"
                  @input="emitAll"
                />
              </div>
              <div class="inst-field inst-pct">
                <div class="pct-input-wrap">
                  <input
                    type="number"
                    class="form-control form-control-sm"
                    v-model.number="localInstallments[index].percentage"
                    min="1"
                    max="100"
                    @input="recalcAmounts"
                  />
                  <span class="pct-symbol">%</span>
                </div>
              </div>
              <div class="inst-field inst-amount">
                <span class="amount-display">
                  ${{ Number(inst.amount || 0).toFixed(2) }}
                </span>
              </div>
              <div class="inst-field inst-date">
                <input
                  type="date"
                  class="form-control form-control-sm"
                  v-model="localInstallments[index].due_date"
                  @change="emitAll"
                  :placeholder="'Auto'"
                />
              </div>
              <div class="inst-field inst-delete">
                <ButtonIcoComponent
                  v-if="localInstallments.length > 1"
                  @onClick="removeInstallment(index)"
                />
              </div>
            </div>
            <div v-if="inst.due_date" class="inst-auto-hint">
              <i class="el-icon-time"></i> Fecha sugerida — puedes modificarla
            </div>
          </div>

          <!-- Switch de cobros para anticipo -->
          <div class="cobro-switch-section mt-3">
            <div class="cobro-switch-row">
              <el-switch
                v-model="enableReminders"
                active-color="#3fdbbf"
                inactive-color="#dcdfe6"
                @change="onToggleReminders"
              ></el-switch>
              <span class="cobro-switch-label">Cobro automático por cuota</span>
              <el-tooltip placement="top" effect="light">
                <div slot="content" class="tooltip-cobro">
                  Cada cuota recibirá recordatorios automáticos:<br />
                  <strong>3 días antes</strong> del vencimiento,<br />
                  <strong>el día del vencimiento</strong>, y<br />
                  <strong>cada viernes</strong> posterior hasta completar 60
                  días.<br /><br />
                  Si no defines fecha de vencimiento en una cuota, se asigna
                  automáticamente según el orden. El cliente puede confirmar su
                  pago desde el correo.
                </div>
                <i class="el-icon-info cobro-info-icon"></i>
              </el-tooltip>
            </div>
            <transition name="slide-fade">
              <div v-if="enableReminders" class="cobro-active-badge">
                <i class="el-icon-bell"></i>
                Recordatorios activos por cada cuota
              </div>
            </transition>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import reminderService from "../../../../reminders/services/ReminderService";
import { limitCredits } from "../../../pages/quotes/assets/resources";
import ButtonIcoComponent from "../../../../components/ButtonIco";

export default {
  name: "InvoicePaymentConditionsComponent",
  components: {
    ButtonIcoComponent
  },
  props: {
    invoiceTotal: { type: Number, default: 0 },
    initialMethodPayment: { type: Number, default: 1 },
    initialLimitCredit: { type: String, default: "" },
    initialLimitCreditAt: { type: String, default: "" },
    initialObservationAdvance: { type: String, default: "" },
    initialReminderSequenceId: { type: Number, default: null },
    initialInstallments: { type: Array, default: () => [] }
  },
  data() {
    return {
      limitCredits,
      sequences: [],
      methodPayment: this.initialMethodPayment,
      limitCredit: this.initialLimitCredit,
      limitCreditAt: this.initialLimitCreditAt,
      observationAdvance: this.initialObservationAdvance,
      enableReminders: false,
      enableInstallments: false,
      reminderSequenceId: this.initialReminderSequenceId,
      localInstallments: this.initialInstallments.length
        ? JSON.parse(JSON.stringify(this.initialInstallments))
        : this.defaultInstallments(),
      activeTpl: "60-40",
      quickTemplates: [
        { key: "60-40", label: "60 / 40" },
        { key: "50-50", label: "50 / 50" },
        { key: "40-30-30", label: "40 / 30 / 30" },
        { key: "30-40-30", label: "30 / 40 / 30" },
        { key: "33-33-34", label: "33 / 33 / 34" }
      ]
    };
  },
  computed: {
    invoiceSequences() {
      return this.sequences.filter(s => s.type === "invoice");
    },
    selectedSequence() {
      if (!this.reminderSequenceId) return null;
      return this.sequences.find(s => s.id === this.reminderSequenceId);
    },
    totalPercentage() {
      return this.localInstallments.reduce(
        (sum, i) => sum + (Number(i.percentage) || 0),
        0
      );
    }
  },
  watch: {
    invoiceTotal() {
      if (this.methodPayment === 3) this.recalcAmounts();
    }
  },
  methods: {
    onMethodPaymentChange(val) {
      if (val === 1) {
        this.enableReminders = false;
        this.enableInstallments = false;
        this.reminderSequenceId = null;
        this.limitCredit = "";
        this.limitCreditAt = "";
        this.observationAdvance = "";
      } else if (val === 2) {
        this.enableInstallments = false;
        this.observationAdvance = "";
      } else if (val === 3) {
        this.enableReminders = true;
        this.enableInstallments = true;
        this.reminderSequenceId = null;
        this.limitCredit = "";
        this.limitCreditAt = "";
        this.applyTemplate("60-40");
      }
      this.emitAll();
    },

    onLimitCreditChange() {
      this.enableReminders = true;
      this.autoAssignSequence(this.limitCredit);
      this.emitAll();
    },

    onToggleReminders(val) {
      if (val) {
        if (this.methodPayment === 2) {
          this.autoAssignSequence(this.limitCredit);
        } else if (this.methodPayment === 3) {
          this.autoAssignInstallmentReminders();
        }
      } else {
        this.reminderSequenceId = null;
        if (this.methodPayment === 3) {
          this.localInstallments.forEach(inst => {
            inst.reminder_sequence_id = null;
          });
        }
      }
      this.emitAll();
    },

    // ─── Auto-assignment ───
    autoAssignSequence(creditTerm) {
      const match = creditTerm ? creditTerm.match(/p-(\d+)/) : null;
      if (match) {
        const days = match[1];
        const found = this.invoiceSequences.find(s =>
          s.name.toLowerCase().includes(days)
        );
        if (found) {
          this.reminderSequenceId = found.id;
          return;
        }
      }
      const def = this.invoiceSequences.find(s => s.is_default);
      this.reminderSequenceId = def ? def.id : null;
    },

    autoAssignInstallmentReminders() {
      const def = this.invoiceSequences.find(s => s.is_default);
      const defId = def ? def.id : null;
      this.localInstallments.forEach(inst => {
        inst.reminder_sequence_id = defId;
      });
    },

    // ─── Installments ───
    calcDueDate(index) {
      const base = new Date();
      const FIRST_DAYS = 7;
      const INTERVAL_DAYS = 30;
      const days = FIRST_DAYS + index * INTERVAL_DAYS;
      base.setDate(base.getDate() + days);
      return base.toISOString().slice(0, 10);
    },

    defaultInstallments() {
      return [
        {
          label: "Anticipo",
          percentage: 60,
          amount: 0,
          due_date: this.calcDueDate(0),
          reminder_sequence_id: null,
          sort_order: 0
        },
        {
          label: "Entrega final",
          percentage: 40,
          amount: 0,
          due_date: this.calcDueDate(1),
          reminder_sequence_id: null,
          sort_order: 1
        }
      ];
    },

    getDefaultLabel(index) {
      return (
        ["Anticipo", "Segunda cuota", "Tercera cuota", "Cuota final"][index] ||
        "Cuota " + (index + 1)
      );
    },

    addInstallment() {
      this.activeTpl = "";
      const idx = this.localInstallments.length;
      const def = this.invoiceSequences.find(s => s.is_default);
      this.localInstallments.push({
        label: this.getDefaultLabel(idx),
        percentage: 0,
        amount: 0,
        due_date: this.calcDueDate(idx),
        reminder_sequence_id: this.enableReminders && def ? def.id : null,
        sort_order: idx
      });
      this.emitAll();
    },

    removeInstallment(index) {
      this.activeTpl = "";
      this.localInstallments.splice(index, 1);
      this.localInstallments.forEach((inst, i) => (inst.sort_order = i));
      this.recalcAmounts();
    },

    recalcAmounts() {
      this.localInstallments.forEach(inst => {
        inst.amount = parseFloat(
          ((Number(inst.percentage) / 100) * this.invoiceTotal).toFixed(2)
        );
      });
      this.emitAll();
    },

    applyTemplate(key) {
      this.activeTpl = key;
      const tpls = {
        "60-40": [
          { l: "Anticipo", p: 60 },
          { l: "Entrega final", p: 40 }
        ],
        "50-50": [
          { l: "Anticipo", p: 50 },
          { l: "Entrega final", p: 50 }
        ],
        "40-30-30": [
          { l: "Anticipo", p: 40 },
          { l: "Segunda cuota", p: 30 },
          { l: "Entrega final", p: 30 }
        ],
        "30-40-30": [
          { l: "Anticipo", p: 30 },
          { l: "Avance", p: 40 },
          { l: "Entrega final", p: 30 }
        ],
        "33-33-34": [
          { l: "Primera cuota", p: 33 },
          { l: "Segunda cuota", p: 33 },
          { l: "Cuota final", p: 34 }
        ]
      };
      const tpl = tpls[key];
      if (!tpl) return;
      const def = this.invoiceSequences.find(s => s.is_default);
      this.localInstallments = tpl.map((t, i) => ({
        label: t.l,
        percentage: t.p,
        amount: parseFloat(((t.p / 100) * this.invoiceTotal).toFixed(2)),
        due_date: this.calcDueDate(i),
        reminder_sequence_id: this.enableReminders && def ? def.id : null,
        sort_order: i
      }));
      this.emitAll();
    },

    async loadSequences() {
      try {
        const res = await reminderService.getSequences();
        if (res.data && res.data.data) {
          this.sequences = res.data.data;
        }
      } catch (e) {
        this.sequences = [];
      }
    },

    emitAll() {
      this.$emit("onChange", {
        method_payment: this.methodPayment,
        limit_credit: this.methodPayment === 2 ? this.limitCredit : "",
        limit_credit_at: this.methodPayment === 2 ? this.limitCreditAt : "",
        observation_advance:
          this.methodPayment === 3 ? this.observationAdvance : "",
        enable_reminders: this.enableReminders,
        enable_installments: this.enableInstallments,
        reminder_sequence_id:
          this.enableReminders && !this.enableInstallments
            ? this.reminderSequenceId
            : null,
        installments: this.enableInstallments ? this.localInstallments : []
      });
    }
  },
  mounted() {
    this.loadSequences();
    if (this.methodPayment === 2 && this.limitCredit) {
      this.enableReminders = !!this.initialReminderSequenceId;
    } else if (this.methodPayment === 3) {
      this.enableReminders = true;
      this.enableInstallments = true;
      this.recalcAmounts();
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3fdbbf;
$bg: #f7f8f9;
$border: #e4eaf6;
$muted: #909399;

.payment-conditions-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 18px 20px;
}

// ── Header ──
.payment-section-header {
  font-weight: 600;
  font-size: 14px;
  color: $primary;
  margin-bottom: 12px;
  i {
    color: $accent;
    margin-right: 6px;
  }
}

// ── Radio group ──
.payment-radio-group {
  width: 100%;
  display: flex;
  margin-bottom: 14px;
  .el-radio-button {
    flex: 1;
  }
  ::v-deep .el-radio-button__inner {
    width: 100%;
    font-size: 13px;
    padding: 9px 12px;
  }
  ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
    background-color: $primary;
    border-color: $primary;
  }
}

// ── Panels ──
.panel-contado {
  background: #e4fff4;
  border-left: 3px solid $accent;
  border-radius: 0 6px 6px 0;
  padding: 10px 14px;
  font-size: 13px;
  color: #2e7d32;
  i {
    color: $accent;
    margin-right: 6px;
  }
}
.panel-credito,
.panel-anticipo {
  padding-top: 4px;
}

// ── Fields ──
.field-label {
  font-size: 12px;
  font-weight: 600;
  color: $primary;
  margin-bottom: 4px;
  display: block;
}

// ── Cobro switch ──
.cobro-switch-section {
  border-top: 1px solid $border;
  padding-top: 12px;
  margin-top: 12px;
}
.cobro-switch-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cobro-switch-label {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
}
.cobro-info-icon {
  font-size: 15px;
  color: $muted;
  cursor: pointer;
  &:hover {
    color: $accent;
  }
}
.tooltip-cobro {
  font-size: 12px;
  line-height: 1.6;
  max-width: 280px;
  color: #333;
}
.cobro-active-badge {
  margin-top: 8px;
  background: #e4fff4;
  border-left: 3px solid $accent;
  border-radius: 0 6px 6px 0;
  padding: 7px 12px;
  font-size: 12px;
  color: #2e7d32;
  i {
    color: $accent;
    margin-right: 4px;
  }
}

// ── Installments ──
.installments-section {
  border-top: 1px solid $border;
  padding-top: 12px;
  margin-top: 8px;
}
.installments-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.pct-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 10px;
  &.pct-ok {
    background: #e4fff4;
    color: #2e7d32;
  }
  &.pct-warn {
    background: #fff6e6;
    color: #e6a23c;
  }
}

// ── Quick templates ──
.quick-tpl {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}
.tpl-btn {
  background: #fff;
  border: 1px solid $border;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  color: $primary;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: $accent;
    color: $accent;
  }
  &.active {
    background: $primary;
    border-color: $primary;
    color: #fff;
  }
  &.tpl-custom {
    border-style: dashed;
    color: $muted;
    i {
      font-size: 11px;
    }
    &:hover {
      border-color: $accent;
      color: $accent;
    }
  }
}

// ── Installment rows ──
.inst-row {
  background: #fff;
  border: 1px solid $border;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 6px;
}
.inst-row-main {
  display: flex;
  align-items: center;
  gap: 8px;
}
.inst-field {
  min-width: 0;
}
.inst-label {
  flex: 2;
}
.inst-pct {
  flex: 0 0 70px;
}
.inst-amount {
  flex: 0 0 80px;
  text-align: right;
}
.inst-date {
  flex: 0 0 120px;
}
.inst-delete {
  flex: 0 0 28px;
}
.pct-input-wrap {
  position: relative;
  input {
    padding-right: 20px;
  }
}
.pct-symbol {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: $muted;
  pointer-events: none;
}
.amount-display {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  white-space: nowrap;
}
.btn-delete {
  background: none;
  border: none;
  color: #ddd;
  font-size: 14px;
  cursor: pointer;
  padding: 2px;
  transition: color 0.15s;
  &:hover:not(:disabled) {
    color: #f56c6c;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
}
.inst-auto-hint {
  font-size: 11px;
  color: $muted;
  margin-top: 4px;
  padding-left: 2px;
  i {
    font-size: 11px;
    margin-right: 2px;
  }
}

// ── Responsive: cuotas stack en tablet/mobile ──
@media (max-width: 991px) {
  .inst-row {
    position: relative;
    padding-right: 36px;
  }
  .inst-row-main {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .inst-label {
    grid-column: 1 / -1;
  }
  .inst-pct {
    grid-column: 1;
  }
  .inst-amount {
    grid-column: 2;
    display: flex;
    align-items: center;
  }
  .inst-date {
    grid-column: 1 / -1;
  }
  .inst-delete {
    position: absolute;
    top: 8px;
    right: 4px;
  }
}

// ── Transitions ──
.slide-fade-enter-active {
  transition: all 0.25s ease;
}
.slide-fade-leave-active {
  transition: all 0.15s ease;
}
.slide-fade-enter {
  transform: translateY(-6px);
  opacity: 0;
}
.slide-fade-leave-to {
  opacity: 0;
}
</style>
